/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState } from 'react';
import { inputStyle, monoLabel } from '../lib/ui.js';

const p = appPalette;

/**
 * Click-to-edit text. Reverts on Esc, commits on Enter / blur. Caller
 * is responsible for trimming / validating / rejecting empty before
 * forwarding to the API.
 */
export function EditableText({
  value,
  onCommit,
  ariaLabel,
  renderStyle,
  inputStyle: localInputStyle,
  placeholder,
}: {
  value: string;
  onCommit: (next: string) => void;
  ariaLabel: string;
  renderStyle: React.CSSProperties;
  inputStyle: React.CSSProperties;
  /** Rendered (muted, italic) when value is empty so the user has
   * something to click on. The placeholder is never persisted. */
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    const showPlaceholder = value === '' && placeholder !== undefined;
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setDraft(value);
            setEditing(true);
          }
        }}
        title="Click to edit"
        style={
          showPlaceholder
            ? { ...renderStyle, cursor: 'text', opacity: 0.55, fontStyle: 'italic' }
            : { ...renderStyle, cursor: 'text' }
        }
      >
        {showPlaceholder ? placeholder : value}
      </span>
    );
  }

  return (
    <input
      type="text"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        onCommit(draft);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onCommit(draft);
          setEditing(false);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setDraft(value);
          setEditing(false);
        }
      }}
      aria-label={ariaLabel}
      style={localInputStyle}
    />
  );
}

/**
 * Click-to-edit estimated minutes. Blank/zero/invalid → null. Caller
 * gets a number or null.
 */
export function EditableMinutes({
  value,
  onCommit,
}: {
  value: number | null;
  onCommit: (next: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value !== null ? String(value) : '');

  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => {
          setDraft(value !== null ? String(value) : '');
          setEditing(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setDraft(value !== null ? String(value) : '');
            setEditing(true);
          }
        }}
        title="Click to edit estimate"
        style={{
          ...monoLabel,
          color: p.muted,
          minWidth: 50,
          textAlign: 'right',
          cursor: 'text',
        }}
        aria-label="Edit estimated minutes"
      >
        {value !== null ? `${value}m` : '—'}
      </span>
    );
  }

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === '') {
      onCommit(null);
    } else {
      const n = Number.parseInt(trimmed, 10);
      if (Number.isFinite(n) && n > 0 && n <= 600) onCommit(n);
      // out-of-range → revert silently
    }
    setEditing(false);
  };

  return (
    <input
      type="number"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setDraft(value !== null ? String(value) : '');
          setEditing(false);
        }
      }}
      min={1}
      max={600}
      aria-label="Edit estimated minutes"
      style={{ ...inputStyle, width: 70, fontSize: 13, padding: '6px 8px', textAlign: 'right' }}
    />
  );
}
